import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, addDoc, deleteDoc, doc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { TABLE_ICONS } from '../utils/tableIcons';
import { useSnackbar } from '../hooks/useSnackbar';
import Snackbar from '../components/Snackbar';
import './DefaultFrames.css';

const DefaultFrames = () => {
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();

  const triggerFileInput = (selector) => {
    const input = document.querySelector(selector);
    if (input) {
      input.click();
    } else {
      console.warn(`File input with selector "${selector}" not found`);
    }
  };

  const [activeTab, setActiveTab] = useState('defaultFrames');
  const [frames, setFrames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFrameForm, setShowFrameForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [frameToDelete, setFrameToDelete] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [filePreviews, setFilePreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [frameFormData, setFrameFormData] = useState({});

  useEffect(() => {
    fetchFrames();
  }, [activeTab]);

  const fetchFrames = useCallback(async () => {
    try {
      setLoading(true);
      const collectionName = activeTab === 'defaultFrames' ? 'defaultFrames' : 'defaultcustomframe';
      const framesCollection = collection(db, collectionName);
      const framesSnapshot = await getDocs(framesCollection);
      const framesList = framesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setFrames(framesList);
    } catch (error) {
      console.error('Error fetching frames:', error);
      showSnackbar('Error fetching frames', 'error');
    } finally {
      setLoading(false);
    }
  }, [showSnackbar, activeTab]);

  const validateFiles = (files) => {
    const validFiles = [];
    const errors = [];

    Array.from(files).forEach(file => {
      if (file.size > 10 * 1024 * 1024) {
        errors.push(`${file.name}: File size must be less than 10MB`);
      } else if (!file.type.startsWith('image/')) {
        errors.push(`${file.name}: Please select an image file`);
      } else {
        validFiles.push(file);
      }
    });

    if (errors.length > 0) {
      showSnackbar(errors[0], 'error');
    }

    return validFiles;
  };

  const handleFileSelect = (event) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const validFiles = validateFiles(files);
      if (validFiles.length > 0) {
        setSelectedFiles([...selectedFiles, ...validFiles]);
        const newPreviews = validFiles.map(file => URL.createObjectURL(file));
        setFilePreviews([...filePreviews, ...newPreviews]);
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const validFiles = validateFiles(files);
      if (validFiles.length > 0) {
        setSelectedFiles([...selectedFiles, ...validFiles]);
        const newPreviews = validFiles.map(file => URL.createObjectURL(file));
        setFilePreviews([...filePreviews, ...newPreviews]);
      }
    }
  };

  const removeFile = (index) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    const newPreviews = filePreviews.filter((_, i) => i !== index);
    setSelectedFiles(newFiles);
    setFilePreviews(newPreviews);
  };

  const clearAllFiles = () => {
    setSelectedFiles([]);
    setFilePreviews([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedFiles.length === 0) {
      showSnackbar('Please select at least one image file', 'error');
      return;
    }

    try {
      setUploading(true);
      let successCount = 0;
      let errorCount = 0;

      const collectionName = activeTab === 'defaultFrames' ? 'defaultFrames' : 'defaultcustomframe';
      const storagePath = activeTab === 'defaultFrames' ? 'defaultFrames' : 'defaultcustomframe';

      for (let i = 0; i < selectedFiles.length; i++) {
        try {
          const file = selectedFiles[i];
          const fileName = `frame_${Date.now()}_${i}_${file.name}`;
          const storageRef = ref(storage, `${storagePath}/${fileName}`);
          await uploadBytes(storageRef, file);
          const imageUrl = await getDownloadURL(storageRef);

          const frameData = {
            imageUrl,
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now()
          };

          await addDoc(collection(db, collectionName), frameData);
          successCount++;
        } catch (error) {
          console.error('Error uploading file:', error);
          errorCount++;
        }
      }

      if (successCount > 0) {
        showSnackbar(`${successCount} frame(s) uploaded successfully`, 'success');
      }
      if (errorCount > 0) {
        showSnackbar(`${errorCount} frame(s) failed to upload`, 'error');
      }

      resetForm();
      fetchFrames();
    } catch (error) {
      console.error('Error saving frames:', error);
      showSnackbar('Error saving frames', 'error');
    } finally {
      setUploading(false);
    }
  };


  const handleDelete = (frame) => {
    setFrameToDelete(frame);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      const collectionName = activeTab === 'defaultFrames' ? 'defaultFrames' : 'defaultcustomframe';
      await deleteDoc(doc(db, collectionName, frameToDelete.id));
      showSnackbar('Frame deleted successfully', 'success');
      fetchFrames();
    } catch (error) {
      console.error('Error deleting frame:', error);
      showSnackbar('Error deleting frame', 'error');
    } finally {
      setShowDeleteConfirm(false);
      setFrameToDelete(null);
    }
  };

  const resetForm = () => {
    setFrameFormData({});
    setSelectedFiles([]);
    setFilePreviews([]);
    setShowFrameForm(false);
  };

  return (
    <div className="default-frames-page">
      <div className="page-header">
        <h2>Default Frames</h2>
        <button 
          className="btn btn-primary"
          onClick={() => setShowFrameForm(true)}
        >
          Add Frame
        </button>
      </div>

      {/* Tabs */}
      <div className="tabs-container">
        <button 
          className={`tab-btn ${activeTab === 'defaultFrames' ? 'active' : ''}`}
          onClick={() => setActiveTab('defaultFrames')}
        >
          Default Frames
        </button>
        <button 
          className={`tab-btn ${activeTab === 'defaultcustomframe' ? 'active' : ''}`}
          onClick={() => setActiveTab('defaultcustomframe')}
        >
          Default Custom Frame
        </button>
      </div>

      {loading ? (
        <div className="loading">Loading frames...</div>
      ) : (
        <div className="frames-grid">
          {frames.length === 0 ? (
            <div className="no-data">No frames found. Add your first frame!</div>
          ) : (
            frames.map(frame => (
              <div key={frame.id} className="frame-card">
                <div className="frame-image">
                  <img src={frame.imageUrl} alt="Frame" />
                  <button 
                    className="delete-overlay-btn"
                    onClick={() => handleDelete(frame)}
                    title="Delete Frame"
                  >
                    {TABLE_ICONS.DELETE}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Frame Form Modal */}
      {showFrameForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Add New Frame</h3>
              <button className="close-btn" onClick={resetForm}>×</button>
            </div>
            <form onSubmit={handleSubmit} className="modal-body">
              <div className="form-group">
                <label>Frame Images *</label>
                <div 
                  className="multi-file-upload-area"
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                >
                  <input
                    type="file"
                    className="file-input"
                    accept="image/*"
                    multiple
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                  
                  {selectedFiles.length > 0 ? (
                    <div className="files-preview-grid">
                      {filePreviews.map((preview, index) => (
                        <div key={index} className="file-preview-item">
                          <img src={preview} alt={`Preview ${index + 1}`} className="preview-thumbnail" />
                          <button
                            type="button"
                            className="remove-file-btn"
                            onClick={() => removeFile(index)}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                      
                      <div 
                        className="add-more-card"
                        onClick={() => triggerFileInput('.file-input')}
                      >
                        <div className="add-more-icon">+</div>
                        <p>Add More</p>
                      </div>
                    </div>
                  ) : (
                    <div 
                      className="upload-placeholder"
                      onClick={() => triggerFileInput('.file-input')}
                    >
                      <div className="upload-icon">📁</div>
                      <p>Click to select frame images or drag & drop</p>
                      <small>Max size: 10MB per file • Multiple files supported</small>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
                <button 
                  type="button"
                  className="btn btn-secondary"
                  onClick={clearAllFiles}
                  disabled={selectedFiles.length === 0}
                >
                  Clear All
                </button>
                <button type="submit" className="btn btn-primary" disabled={uploading || selectedFiles.length === 0}>
                  {uploading ? 'Uploading...' : `Upload ${selectedFiles.length} Frame${selectedFiles.length !== 1 ? 's' : ''}`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Confirm Delete</h3>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this frame?</p>
              <p>This action cannot be undone.</p>
            </div>
            <div className="form-actions">
              <button 
                className="btn btn-secondary" 
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </button>
              <button className="btn btn-danger" onClick={confirmDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {snackbar.show && (
        <Snackbar 
          message={snackbar.message}
          type={snackbar.type}
          onClose={hideSnackbar}
        />
      )}
    </div>
  );
};

export default DefaultFrames;
